"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useInfiniteQuery } from "@tanstack/react-query";

import { Icon } from "@/components/ui/icon";
import { Select } from "@/components/ui/select";
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll";
import { useProjectSequencesSheet } from "@/hooks/use-project-sequences-sheet";
import { useSelectedProject } from "@/hooks/use-search";
import { listJobs } from "@/lib/api/generated";
import { useSequenceWizardStore } from "@/features/sequences/store/wizard-store";

const PER_PAGE = 20;

type CreateSequenceJobPickerProps = {
  /** Popover alignment relative to the trigger (toolbar uses `end`). */
  align?: "start" | "center" | "end";
  /**
   * Maps to `Select.Trigger` / `inputVariants` sizes.
   * Toolbar uses `xs`; empty-state keeps `sm`.
   */
  size?: "xs" | "sm";
};

/**
 * Pick a project job, navigate to its Outreach Sequences tab, and open the
 * create-sequence wizard (same pattern as job-create completion → Sequences).
 */
export function CreateSequenceJobPicker({
  align = "center",
  size = "sm",
}: CreateSequenceJobPickerProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { closeProjectSequences } = useProjectSequencesSheet();
  const { data: selectedProject } = useSelectedProject();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [forbidden, setForbidden] = useState(false);

  const prevProjectIdRef = useRef(selectedProject?.id);
  useEffect(() => {
    if (prevProjectIdRef.current !== selectedProject?.id) {
      prevProjectIdRef.current = selectedProject?.id;
      setForbidden(false);
    }
  }, [selectedProject?.id]);

  const jobsQuery = useInfiniteQuery({
    queryKey: [
      "jobs",
      "create-sequence-picker",
      selectedProject?.id,
      search.trim(),
    ],
    queryFn: ({ pageParam }) =>
      listJobs({
        project_id: selectedProject!.id,
        per_page: PER_PAGE,
        search: search.trim() || undefined,
        status: "active",
        cursor: pageParam,
      }),
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.next_cursor || undefined,
    enabled: open && !!selectedProject?.id && !forbidden,
  });

  useEffect(() => {
    const err = jobsQuery.error as { status?: number } | null;
    if (err?.status === 403) setForbidden(true);
  }, [jobsQuery.error]);

  const viewportRef = useRef<HTMLDivElement>(null);
  useInfiniteScroll({
    rootRef: viewportRef,
    hasNextPage: jobsQuery.hasNextPage,
    isFetching: jobsQuery.isFetchingNextPage,
    onLoadMore: jobsQuery.fetchNextPage,
    enabled: open,
  });

  const jobs = jobsQuery.data?.pages.flatMap((p) => p.jobs) ?? [];

  const pickJob = (targetJobId: string) => {
    setOpen(false);
    const alreadyOnTarget = pathname === `/jobs/${targetJobId}/sequences`;

    void (async () => {
      // Close the project sheet first so it isn't still covering the page
      // when the wizard opens. Await nuqs so we don't race router.push.
      await closeProjectSequences();

      if (alreadyOnTarget) {
        useSequenceWizardStore.getState().openWizard(targetJobId);
        return;
      }

      // Queue the wizard until the destination job sequences page is active
      // (SequenceWizard is already mounted on other jobs' sequences routes).
      useSequenceWizardStore.getState().queueOpenWizard(targetJobId);
      router.push(`/jobs/${targetJobId}/sequences`);
    })();
  };

  return (
    <Select.Root
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) setSearch("");
      }}
    >
      <Select.Trigger
        size={size}
        variant="none"
        className="w-auto bg-contrast gap-0.5 font-medium text-background shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] hover:bg-contrast/90"
      >
        <span>Create Sequence</span>
        <Icon
          name="IconPlusSmall"
          className={size === "xs" ? "size-5" : "size-6"}
          aria-hidden
        />
      </Select.Trigger>

      <Select.Content
        matchTriggerWidth={false}
        className="w-[320px] max-w-[320px] overflow-x-hidden"
        align={align}
        sideOffset={8}
      >
        <Select.Search
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search jobs…"
          autoFocus
        />
        <Select.List viewportRef={viewportRef} constrainContentWidth>
          {forbidden ? (
            <div className="flex flex-col items-center justify-center gap-0.5 px-3 py-6">
              <p className="text-sm font-medium text-heading">No access</p>
              <p className="mt-0.5 text-center text-xs leading-normal text-contrast/80">
                You don&apos;t have permission to view jobs in this project.
              </p>
            </div>
          ) : jobsQuery.isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Icon
                name="IconLoadingCircle"
                size={18}
                className="animate-spin text-contrast/40"
              />
            </div>
          ) : jobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-0.5 px-3 py-6">
              <p className="text-sm font-medium text-heading">No jobs found</p>
              <p className="mt-0.5 text-center text-xs leading-normal text-contrast/80">
                {search.trim()
                  ? "No matching jobs. Try a different search."
                  : "Create a job in this project first."}
              </p>
            </div>
          ) : (
            <>
              {jobs.map((job, i) => (
                <Select.Item
                  key={job.id}
                  index={i}
                  onSelect={() => pickJob(job.id)}
                >
                  <Select.ItemIcon>
                    <Icon
                      name="IconBag"
                      size={14}
                      className="text-heading opacity-60"
                    />
                  </Select.ItemIcon>
                  <Select.ItemLabel className="min-w-0">
                    {job.title || "Unnamed"}
                  </Select.ItemLabel>
                </Select.Item>
              ))}
              {jobsQuery.isFetchingNextPage && (
                <div className="flex items-center justify-center py-2">
                  <Icon
                    name="IconLoadingCircle"
                    size={16}
                    className="animate-spin text-contrast/40"
                  />
                </div>
              )}
            </>
          )}
        </Select.List>
      </Select.Content>
    </Select.Root>
  );
}
